import { ref, onMounted, onUnmounted } from 'vue'

import type { Ref } from 'vue'

export interface Dimensions {
  width: number,
  height: number,
  fromTop: number,
  innerWidth: number,
  innerHeight: number
}

/**
 * A composable that measures the dimensions of a DOM element
 *
 * @param target - Element ref to measure.
 * @returns An object containing the measured dimensions
 */
export function useMeasure<T extends HTMLElement = HTMLElement> (
  elementRef: Ref<T | null>
) {
  const dimensions = ref<Dimensions>({
    width: 0,
    height: 0,
    fromTop: 0,
    innerWidth: 0,
    innerHeight: 0
  })

  let observer: ResizeObserver | null = null

  const updateRect = (element: HTMLElement) => {
    const domRect = element.getBoundingClientRect()
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop
    const computedStyle = window.getComputedStyle(element)

    const marginLeft = parseFloat(computedStyle.marginLeft)
    const marginRight = parseFloat(computedStyle.marginRight)
    const marginTop = parseFloat(computedStyle.marginTop)
    const marginBottom = parseFloat(computedStyle.marginBottom)

    dimensions.value = {
      width: domRect.width + marginLeft + marginRight,
      height: domRect.height + marginTop + marginBottom,
      fromTop: domRect.top + scrollTop,
      innerWidth: domRect.width,
      innerHeight: domRect.height
    }
  }

  onMounted(() => {
    if (!window.ResizeObserver) {
      console.warn('ResizeObserver is not supported in this browser')
      return
    }

    observer = new ResizeObserver((entries) => {
      if (!entries.length) return

      const entry = entries[0]
      if (entry) {
        updateRect(entry.target as HTMLElement)
      }
    })

    const element = elementRef.value
    if (element) {
      observer.observe(element)
      updateRect(element)
    }
  })

  onUnmounted(() => {
    if (observer) {
      observer.disconnect()
      observer = null
    }
  })

  return {
    dimensions
  }
}
